import { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Button, Tooltip, TextInput, Modal, Spinner, Checkbox, Alert } from "flowbite-react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  getTags,
  atualizarTag,
  deletarTag,
  getDetalhesTag,
  removerTagDeProdutos,
} from "../../api/produto";
import { showNotification } from "../../actions/notification";
import {
  FaSortAlphaDown,
  FaSortAmountUp,
  FaSortAmountDown,
  FaBoxes,
} from "react-icons/fa";
import { HiOutlinePencil, HiOutlineTrash, HiOutlineEye } from "react-icons/hi";
import { hasPlanAccess } from "../../utils/subscription";

const formatDateTime = (value) => {
  if (!value) return "-";
  return dayjs(value).format("DD/MM/YYYY HH:mm");
};

const normalizeTag = (tag) => ({
  ...tag,
  produtoCount: Number(tag.produtoCount || 0),
  semFotoCount: Number(tag.semFotoCount || 0),
  ultimaAtualizacaoProdutos: tag.ultimaAtualizacaoProdutos || null,
});

const Tags = () => {
  const dispatch = useDispatch();
  const { loja, profile } = useSelector((state) => state.user);
  const assinaturaAtiva = hasPlanAccess(profile);

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortMode, setSortMode] = useState("nome");
  const [searchTerm, setSearchTerm] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [tagEditando, setTagEditando] = useState(null);
  const [tagDeletando, setTagDeletando] = useState(null);
  const [novoNome, setNovoNome] = useState("");
  const [confirmacao, setConfirmacao] = useState("");

  const [modalProdutosOpen, setModalProdutosOpen] = useState(false);
  const [detalhesTag, setDetalhesTag] = useState(null);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [selectedProdutoIds, setSelectedProdutoIds] = useState([]);
  const [removendoVinculos, setRemovendoVinculos] = useState(false);

  const carregarTags = useCallback(async () => {
    if (!loja?.id) return;

    setLoading(true);
    try {
      const response = await getTags();
      setTags((response.data || []).map(normalizeTag));
    } catch (error) {
      dispatch(
        showNotification({
          message: error.message || "Erro ao buscar tags.",
          color: "failure",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, loja?.id]);

  useEffect(() => {
    carregarTags();
  }, [loja?.id]);

  const filteredAndSortedTags = useMemo(() => {
    const termo = searchTerm.trim().toLowerCase();
    const filtradas = tags.filter((tag) => tag.nome.toLowerCase().includes(termo));
    const list = [...filtradas];

    if (sortMode === "nome") {
      list.sort((a, b) => a.nome.localeCompare(b.nome));
    } else if (sortMode === "produtosAsc") {
      list.sort((a, b) => a.produtoCount - b.produtoCount);
    } else if (sortMode === "produtosDesc") {
      list.sort((a, b) => b.produtoCount - a.produtoCount);
    } else if (sortMode === "semFotoDesc") {
      list.sort((a, b) => b.semFotoCount - a.semFotoCount);
    }

    return list;
  }, [tags, searchTerm, sortMode]);

  const abrirModalEdicao = (tag) => {
    setTagEditando(tag);
    setNovoNome(tag.nome);
    setModalOpen(true);
  };

  const salvarTag = async () => {
    if (!assinaturaAtiva || !tagEditando || !novoNome.trim()) return;

    try {
      await atualizarTag(tagEditando.id, novoNome.trim());
      setTags((prev) =>
        prev.map((tag) =>
          tag.id === tagEditando.id ? { ...tag, nome: novoNome.trim() } : tag
        )
      );
      dispatch(
        showNotification({
          message: "Tag atualizada com sucesso.",
          color: "success",
        })
      );
      setModalOpen(false);
    } catch (error) {
      dispatch(
        showNotification({
          message: error.message || "Erro ao atualizar tag.",
          color: "failure",
        })
      );
    }
  };

  const excluirTag = async () => {
    if (
      !assinaturaAtiva ||
      !tagDeletando ||
      confirmacao !== `Deletar ${tagDeletando.nome}`
    )
      return;

    try {
      await deletarTag(tagDeletando.id);
      setTags((prev) => prev.filter((tag) => tag.id !== tagDeletando.id));
      dispatch(
        showNotification({
          message: "Tag excluida com sucesso.",
          color: "success",
        })
      );
      setModalDeleteOpen(false);
      setConfirmacao("");
    } catch (error) {
      dispatch(
        showNotification({
          message: error.message || "Erro ao deletar tag.",
          color: "failure",
        })
      );
    }
  };

  const abrirModalProdutos = async (tag) => {
    setModalProdutosOpen(true);
    setDetalhesTag(null);
    setSelectedProdutoIds([]);
    setLoadingDetalhes(true);

    try {
      const response = await getDetalhesTag(tag.id);
      setDetalhesTag(response.data);
    } catch (error) {
      dispatch(
        showNotification({
          message: error.message || "Erro ao carregar produtos da tag.",
          color: "failure",
        })
      );
      setModalProdutosOpen(false);
    } finally {
      setLoadingDetalhes(false);
    }
  };

  const toggleProdutoSelecionado = (produtoId) => {
    setSelectedProdutoIds((prev) =>
      prev.includes(produtoId)
        ? prev.filter((id) => id !== produtoId)
        : [...prev, produtoId]
    );
  };

  const selecionarTodos = () => {
    if (!detalhesTag?.produtos?.length) return;
    setSelectedProdutoIds(detalhesTag.produtos.map((produto) => produto.id));
  };

  const limparSelecao = () => {
    setSelectedProdutoIds([]);
  };

  const removerTagSelecionadaDosProdutos = async () => {
    if (!assinaturaAtiva || !detalhesTag?.id || selectedProdutoIds.length === 0)
      return;

    setRemovendoVinculos(true);
    try {
      const response = await removerTagDeProdutos(detalhesTag.id, selectedProdutoIds);
      const removidosSet = new Set(selectedProdutoIds);

      setDetalhesTag((prev) => {
        if (!prev) return prev;
        const produtosAtualizados = prev.produtos.filter(
          (produto) => !removidosSet.has(produto.id)
        );
        return {
          ...prev,
          produtos: produtosAtualizados,
          totalProdutos: produtosAtualizados.length,
          semFotoCount: produtosAtualizados.filter((produto) => produto.semFoto).length,
          ultimaAtualizacao:
            produtosAtualizados.length > 0
              ? produtosAtualizados
                  .map((produto) => produto.updatedAt)
                  .sort((a, b) => new Date(b) - new Date(a))[0]
              : null,
        };
      });

      setSelectedProdutoIds([]);
      dispatch(
        showNotification({
          message: response.data?.message || "Vinculos removidos com sucesso.",
          color: "success",
        })
      );

      await carregarTags();
    } catch (error) {
      dispatch(
        showNotification({
          message: error.message || "Erro ao remover tag dos produtos.",
          color: "failure",
        })
      );
    } finally {
      setRemovendoVinculos(false);
    }
  };

  return (
    <div className="overflow-x-auto">
      <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mt-8 lg:mt-2 mb-4 select-none cursor-default">
        Tags
      </h2>
      {!assinaturaAtiva ? (
        <div className="mb-4">
          <Alert color="warning">
            Seu plano esta inativo. Voce pode consultar tags, mas acoes
            de edição/remoção estão bloqueadas até reativar em <b>/app/planos</b>.
          </Alert>
        </div>
      ) : null}

      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
        <TextInput
          placeholder="Pesquisar por nome da tag..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-1/2"
        />
        <div className="flex gap-2">
          <Tooltip content="Ordenar A-Z">
            <Button onClick={() => setSortMode("nome")} gradientDuoTone="purpleToPink" outline={sortMode !== "nome"}>
              <FaSortAlphaDown />
            </Button>
          </Tooltip>
          <Tooltip content="Menor para maior quantidade de produtos">
            <Button
              onClick={() => setSortMode("produtosAsc")}
              gradientDuoTone="purpleToPink"
              outline={sortMode !== "produtosAsc"}
            >
              <FaSortAmountUp />
            </Button>
          </Tooltip>
          <Tooltip content="Maior para menor quantidade de produtos">
            <Button
              onClick={() => setSortMode("produtosDesc")}
              gradientDuoTone="purpleToPink"
              outline={sortMode !== "produtosDesc"}
            >
              <FaSortAmountDown />
            </Button>
          </Tooltip>
          <Tooltip content="Tags com mais produtos sem foto">
            <Button
              onClick={() => setSortMode("semFotoDesc")}
              gradientDuoTone="purpleToPink"
              outline={sortMode !== "semFotoDesc"}
            >
              <FaBoxes />
            </Button>
          </Tooltip>
        </div>
      </div>

      <Table hoverable className="w-full text-left bg-white rounded-lg shadow-md select-none focus:outline-none">
        <Table.Head>
          <Table.HeadCell>Nome</Table.HeadCell>
          <Table.HeadCell>Qtd. Produtos</Table.HeadCell>
          <Table.HeadCell>Sem Foto</Table.HeadCell>
          <Table.HeadCell>Ultima Atualizacao</Table.HeadCell>
          <Table.HeadCell></Table.HeadCell>
        </Table.Head>
        <Table.Body className="divide-y">
          {loading ? (
            <Table.Row>
              <Table.Cell colSpan="5" className="text-center py-8">
                <div className="flex items-center justify-center gap-2 text-gray-500">
                  <Spinner size="sm" /> Carregando tags...
                </div>
              </Table.Cell>
            </Table.Row>
          ) : filteredAndSortedTags.length > 0 ? (
            filteredAndSortedTags.map((tag) => (
              <Table.Row key={tag.id}>
                <Table.Cell>{tag.nome}</Table.Cell>
                <Table.Cell>{tag.produtoCount}</Table.Cell>
                <Table.Cell>{tag.semFotoCount}</Table.Cell>
                <Table.Cell>{formatDateTime(tag.ultimaAtualizacaoProdutos)}</Table.Cell>
                <Table.Cell>
                  <div className="flex gap-2">
                    <Tooltip content="Ver produtos da tag">
                      <Button size="sm" color="light" onClick={() => abrirModalProdutos(tag)}>
                        <HiOutlineEye />
                      </Button>
                    </Tooltip>
                    <Tooltip content="Editar">
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => abrirModalEdicao(tag)}
                        disabled={!assinaturaAtiva}
                      >
                        <HiOutlinePencil />
                      </Button>
                    </Tooltip>
                    <Tooltip content="Excluir">
                      <Button
                        size="sm"
                        color="warning"
                        onClick={() => {
                          setTagDeletando(tag);
                          setModalDeleteOpen(true);
                        }}
                        disabled={!assinaturaAtiva}
                      >
                        <HiOutlineTrash />
                      </Button>
                    </Tooltip>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))
          ) : (
            <Table.Row>
              <Table.Cell colSpan="5" className="text-center text-gray-500">
                Nenhuma tag encontrada.
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      <Modal show={modalOpen} onClose={() => setModalOpen(false)}>
        <Modal.Header>Editar Tag</Modal.Header>
        <Modal.Body>
          <TextInput
            type="text"
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            placeholder="Novo nome da tag"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button color="blue" onClick={salvarTag}>
            Salvar
          </Button>
          <Button color="gray" onClick={() => setModalOpen(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={modalDeleteOpen} onClose={() => setModalDeleteOpen(false)}>
        <Modal.Header>Aviso</Modal.Header>
        <Modal.Body>
          Tem certeza que deseja deletar a tag "{tagDeletando?.nome}"?
          <br />
          Digite "Deletar {tagDeletando?.nome}" para confirmar:
          <TextInput
            type="text"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button color="failure" onClick={excluirTag}>
            Deletar
          </Button>
          <Button color="gray" onClick={() => setModalDeleteOpen(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={modalProdutosOpen} onClose={() => setModalProdutosOpen(false)} size="5xl">
        <Modal.Header>Produtos da Tag</Modal.Header>
        <Modal.Body>
          {loadingDetalhes ? (
            <div className="flex items-center justify-center py-10">
              <Spinner size="lg" />
            </div>
          ) : null}

          {!loadingDetalhes && detalhesTag ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                <div className="border rounded p-2">
                  <p className="text-gray-500">Tag</p>
                  <p className="font-semibold">{detalhesTag.nome}</p>
                </div>
                <div className="border rounded p-2">
                  <p className="text-gray-500">Produtos</p>
                  <p className="font-semibold">{detalhesTag.totalProdutos}</p>
                </div>
                <div className="border rounded p-2">
                  <p className="text-gray-500">Sem Foto</p>
                  <p className="font-semibold">{detalhesTag.semFotoCount}</p>
                </div>
                <div className="border rounded p-2">
                  <p className="text-gray-500">Ultima Atualizacao</p>
                  <p className="font-semibold">{formatDateTime(detalhesTag.ultimaAtualizacao)}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button color="light" size="xs" onClick={selecionarTodos}>
                  Selecionar todos
                </Button>
                <Button color="light" size="xs" onClick={limparSelecao}>
                  Limpar selecao
                </Button>
                <Button
                  color="failure"
                  size="xs"
                  onClick={removerTagSelecionadaDosProdutos}
                  disabled={
                    !assinaturaAtiva ||
                    selectedProdutoIds.length === 0 ||
                    removendoVinculos
                  }
                >
                  {removendoVinculos
                    ? "Removendo..."
                    : `Remover tag de ${selectedProdutoIds.length} produto(s)`}
                </Button>
              </div>

              <div className="max-h-[420px] overflow-auto border rounded-lg">
                <Table>
                  <Table.Head>
                    <Table.HeadCell />
                    <Table.HeadCell>Thumb</Table.HeadCell>
                    <Table.HeadCell>Produto</Table.HeadCell>
                    <Table.HeadCell>Referencia</Table.HeadCell>
                    <Table.HeadCell>Fotos</Table.HeadCell>
                    <Table.HeadCell>Atualizado</Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="divide-y">
                    {detalhesTag.produtos?.length > 0 ? (
                      detalhesTag.produtos.map((produto) => (
                        <Table.Row key={produto.id}>
                          <Table.Cell>
                            <Checkbox
                              checked={selectedProdutoIds.includes(produto.id)}
                              onChange={() => toggleProdutoSelecionado(produto.id)}
                            />
                          </Table.Cell>
                          <Table.Cell>
                            {produto.thumbUrl ? (
                              <img
                                src={`${process.env.REACT_APP_UPLOADS_URL}${produto.thumbUrl}`}
                                alt={produto.descricao}
                                className="h-12 w-12 rounded object-cover"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                                sem foto
                              </div>
                            )}
                          </Table.Cell>
                          <Table.Cell>{produto.descricao}</Table.Cell>
                          <Table.Cell>{produto.referencia || "-"}</Table.Cell>
                          <Table.Cell>{produto.fotoCount}</Table.Cell>
                          <Table.Cell>{formatDateTime(produto.updatedAt)}</Table.Cell>
                        </Table.Row>
                      ))
                    ) : (
                      <Table.Row>
                        <Table.Cell colSpan="6" className="text-center text-gray-500">
                          Nenhum produto vinculado a essa tag.
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setModalProdutosOpen(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Tags;
